import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const WestminsterCondosForSalePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&propertySubType=Condominium&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Westminster condos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Westminster Condos for Sale | Affordable Condominiums | Truc Doan</title>
        <meta name="description" content="Westminster CA condos for sale from $350K-$650K. Affordable condominiums with low maintenance, HOA amenities, and proximity to Little Saigon. Truc Doan helps you find the perfect Westminster condo." />
        <meta name="keywords" content="Westminster condos for sale, condominiums Westminster CA, affordable condos Westminster, Westminster condo communities, HOA Westminster, Little Saigon condos, first time buyer Westminster, Truc Doan condos, Orange County condos affordable" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/westminster-condos-for-sale`} />
        <meta property="og:title" content="Westminster Condos for Sale | Affordable Condominiums | Truc Doan" />
        <meta property="og:description" content="Affordable Westminster CA condos for sale. $350K-$650K range, low maintenance living near Little Saigon. Expert guidance from Truc Doan." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/westminster-condos-for-sale`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/westminster-condos-for-sale`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Find affordable condominiums for sale in Westminster CA with Truc Doan. Expert condo buying guidance near Little Saigon.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Westminster Condos for Sale – Affordable Condo Living Near Little Saigon</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Discover affordable condominiums in Westminster CA with low maintenance living and community amenities. Median price ~$500K.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Choose Condo Living in Westminster?</h2>
          <p>Westminster condos offer an affordable entry point into Orange County homeownership with the added benefits of low-maintenance living, community amenities, and proximity to Little Saigon's cultural hub. With a median condo price around $500,000, condominiums are significantly more affordable than single-family homes while still providing the pride of ownership.</p>
          <p>Condo communities in Westminster feature pools, fitness centers, gated security, and landscaped common areas – all maintained by your HOA so you can enjoy your weekends instead of doing yard work.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Benefits of Westminster Condo Ownership</h2>
          <p>Condominiums offer unique advantages for first-time buyers, downsizers, and investors alike.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Affordable Entry to Homeownership</h3>
              <p>With condos starting in the $350K range, Westminster offers first-time buyers an affordable path to homeownership in Orange County without compromising on location or community.</p>
            </div>
            <div className="amenity">
              <h3>Low Maintenance Living</h3>
              <p>HOA covers exterior maintenance, landscaping, roofing, and common area upkeep. Spend your time enjoying Westminster's restaurants and cultural offerings instead of home repairs.</p>
            </div>
            <div className="amenity">
              <h3>Community Amenities</h3>
              <p>Many Westminster condo communities feature swimming pools, spas, fitness centers, clubhouses, and gated security – amenities that would cost significantly more in a single-family home.</p>
            </div>
            <div className="amenity">
              <h3>Prime Location Near Little Saigon</h3>
              <p>Westminster condos put you minutes from Bolsa Avenue's restaurants, shops, and cultural centers while maintaining quiet residential settings.</p>
            </div>
            <div className="amenity">
              <h3>Popular Condo Communities</h3>
              <p>Well-established communities with strong HOA reserves, well-maintained grounds, and active community engagement make Westminster a top choice for condo buyers.</p>
            </div>
            <div className="amenity">
              <h3>Investment Potential</h3>
              <p>Westminster condos appreciate steadily and attract strong rental demand, making them excellent starter investments or future rental properties.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Westminster Condo Market Snapshot</h2>
          <p>The Westminster condo market offers excellent value with quick sales times and steady appreciation.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Condo Price</td><td>~$500,000</td></tr>
              <tr><td>Average Days on Market</td><td>18–25 Days</td></tr>
              <tr><td>Popular Price Range</td><td>$350K–$650K</td></tr>
              <tr><td>HOA Fees (typical)</td><td>$300–$500/mo</td></tr>
              <tr><td>vs Single-Family Median</td><td>~$250K less</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Westminster Condos Currently for Sale</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Browse all available Westminster condos by clicking "Search Listings" below.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Find Your Westminster Condo?</h2>
          <p>Contact Truc Doan today for expert condo buying guidance. Get pre-approved, explore communities, and find your perfect Westminster condominium.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Westminster Condo Inquiry" className="cta-button primary">Email About Condos</a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">Call +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Condo Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default WestminsterCondosForSalePage;
